import { usePageContent, useContentSingletons, useXcoreUtils } from "@robe/client/xcore-ui";
import { Box, Container, Flex } from "@xcorejs/ui";
import PdfIcon from "components/Icons/16/pdf.svg";
import ImagesCarousel from "components/Carousel/ImagesCarousel";
import Layout from "components/Layout";
import RobeHead from "components/RobeHead";
import { productCardProductFields } from "design-system/robe/Cards/Product";
import { useTagManager } from "hooks/useTagManager";
import { getRobePaths, getRobeProps, RobePage } from "xcore";
import { News, NewsDetailPage } from "xcore/types";
import { useRouter } from "next/router";
import { fetchRelationsForSingleContent, SubCategory } from "@robe/core";
import { useNewsGoogleStructuredData } from "utils/googleStructuredData/useNewsGoogleStructuredData";
import { IconButton } from "design-system";
import { usePdfCdn } from "hooks";
import { MoreNews, NewsArticle } from "components/News";

export const getStaticProps = getRobeProps<News>(async ({ core, id, locale }) => {
  const pageContentFetched = await core.content.singleById<News>(id!, {
    fields: ["title", "products", "intro", "content", "images", "buzzsproutEpisodeId"],
    includeCategories: true,
    // todo xcc after localized images migrated remove workaround
    ...(locale === "ru" && {
      locales: ["ru", "en"],
    }),
  });

  const pageContent = await fetchRelationsForSingleContent<News>({
    core,
    relationSourceFields: [{ field: "products" }],
    options: {
      includeCategories: true,
      fields: productCardProductFields,
      // todo xcc after localized images migrated remove workaround
      ...(locale === "ru" && {
        locales: ["ru", "en"],
      }),
    },
  })(pageContentFetched);

  let categories: number[] = [];
  if (pageContent?.subCategories && "news-categories" in pageContent.subCategories) {
    categories = (pageContent.subCategories as Record<string, SubCategory[]>)["news-categories"].map((c) => c.id);
  }

  return {
    pageContent,
    multipleContents: [
      core.content.multipleByType<News>("news", {
        fields: ["title", "intro", "images", "layout", "thumbnail"],
        ...(categories.length > 0 && { categories: [categories] }),
        includeCategories: true,
        sort: {
          fieldName: "createdAt",
          orderBy: "Descending",
        },
        perPage: 5,
      }),
    ],
    contentSingletons: [
      core.content.singletonByType<NewsDetailPage>("newsDetailPage", {
        fields: ["productInvolved", "showPdfBtn", "discoverMoreNews"],
      }),
    ],
  };
});

export const getStaticPaths = getRobePaths("news");

const NewsPage: RobePage = () => {
  const { locale } = useRouter();
  const { stringify, image, localize } = useXcoreUtils();
  const news = usePageContent<News>();
  const [newsDetailPage] = useContentSingletons<[NewsDetailPage]>();
  const { values } = news;

  useTagManager("news-detail");

  const newsGoogleStructuredData = useNewsGoogleStructuredData();

  const newsPdfUrl = usePdfCdn({ id: news.id, locale, type: "news" });

  // todo xcc after localized images migrated remove workaround
  const imagesResolved = values.images?.en ?? localize(values.images);

  const headImg = values?.thumbnail
    ? image(values.thumbnail, { width: 600, height: 450 })
    : imagesResolved
      ? image(imagesResolved[0], { width: 1290, height: 726 })
      : null;

  const ogImageSrc = values?.thumbnail ? image(values.thumbnail, { width: 600, height: 450 }) : null;
  const fallbackOGImageSrc = imagesResolved?.[0] ? image(imagesResolved?.[0], { width: 1290, height: 726 }) : null;

  return (
    <>
      <RobeHead
        googleStructuredData={newsGoogleStructuredData}
        title={[stringify(values.title)]}
        description={stringify(values.title)}
        {...(headImg && { img: headImg })}
        img={ogImageSrc ?? fallbackOGImageSrc ?? undefined}
      />

      <Container flexDirection="column" paddingTop={{ _: "3rem", sm: "5rem" }} paddingBottom={{ _: "6rem", sm: "10rem" }}>
        <NewsArticle news={news} newsDetailPage={newsDetailPage} />

        {imagesResolved && (
          <Box mt={{ _: "4rem", sm: "8rem" }} mb={{ _: "2rem", sm: "8rem" }}>
            <ImagesCarousel
              disableScroll
              hideSubHeader
              images={imagesResolved.map((img) => ({
                src: image(img, imagesResolved.length > 1 ? { width: 740, height: 416 } : { width: 1290, height: 726 }),
                alt: img.id,
                retina: image(
                  img,
                  imagesResolved.length > 1 ? { width: 740, height: 416, enlargement: 2 } : { width: 1290, height: 726, enlargement: 2 }
                ),
              }))}
            />
          </Box>
        )}
        {newsPdfUrl && (
          <Flex justifyContent="center" flexWrap="wrap" width="100%">
            <IconButton
              as="a"
              mb="8rem"
              target="_blank"
              href={newsPdfUrl}
              bg="black"
              icon={<PdfIcon />}
              boxShadow="0 3.5rem 2.5rem -2.5rem rgba(30, 30, 30, 1)"
            >
              {stringify(newsDetailPage.values.showPdfBtn)}
            </IconButton>
          </Flex>
        )}
        <MoreNews />
      </Container>
    </>
  );
};

NewsPage.Layout = Layout;

export default NewsPage;
